import {
  alpha,
  Box,
  ClickAwayListener,
  IconButton,
  InputBase,
  Paper,
  Stack,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { SearchIcon } from "../../../../../../assets/header/headerIcon";
import CloseMenuIcon from "../../../../../../assets/menu/closeMenuIcon";
import { MENU } from "../../../../../../locales/components/namespaces";
import SearchResults from "./searchResults";
import axios from "axios";
import {API} from "../../../../../../helpers/constants";
import useUserData from "../../../../../../hooks/user/useUserData";

const HeaderSearch = () => {
  const { t } = useTranslation(MENU);
  const theme = useTheme();
  const history = useHistory();
const {language} = useUserData()
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchInputRef = useRef(null);
  const searchIconRef = useRef(null);

  useEffect(() => {
    if (!isSearchOpen) return;

    const timeout = setTimeout(async () => {
      const value = searchQuery.trim();

      if (value.length < 3) {
        setSearchResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      const res = await axios({
        method: "post",
        url: `${API}/en/brew/item/search`,
        data: {
          search: value,
        },
      });
      const items = res.data?.data?.items ?? [];

      setSearchResults(
          items.map((item) => ({
            id: item.id,
            title: language === "el"
                ? (item.nameEL ?? item.nameEN ?? "")
                : (item.nameEN ?? item.nameEL ?? ""),
            price: item.price ? `${item.price} €` : "",
            image: item.image ?? "",
          }))
      );
      setLoading(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery, isSearchOpen]);

  const handleSearchOpen = () => {
    setIsSearchOpen(true);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setLoading(false);
  };

  const handleClickAway = (event) => {
    if (event.target.closest("[aria-label='search']")) return;
    handleSearchClose();
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter" && searchResults.length > 0) {
      history.push("/products");
      handleSearchClose();
    }
  };

  const handleProductClick = (productId) => {
    history.push(`/product/${productId}`);
    handleSearchClose();
  };

  return (
      <Stack direction="row" alignItems="center" ref={searchIconRef}>
        <IconButton
            aria-label="search"
            onClick={handleSearchOpen}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              p: "10px",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
        >
          <SearchIcon
              sx={{
                width: 20,
                height: 20,
                transition: "all 0.3s ease",
              }}
          />
        </IconButton>

        <ClickAwayListener onClickAway={handleClickAway}>
          <Box>
            {isSearchOpen && (
                <Box
                    sx={{
                      position: "fixed",
                      zIndex: 1202,
                      animation: "slideInRight 0.3s ease",
                      "@keyframes slideInRight": {
                        from: {
                          opacity: 0,
                          transform: "translateX(10px)",
                        },
                        to: {
                          opacity: 1,
                          transform: "translateX(0)",
                        },
                      },
                      left: searchIconRef.current
                          ? searchIconRef.current.getBoundingClientRect().left - 140
                          : "auto",
                      top:
                          (searchIconRef.current
                              ? searchIconRef.current.getBoundingClientRect().bottom
                              : 0) + 6,
                    }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <Paper
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#ffffff",
                          borderRadius: "5px",
                          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                          px: 2,
                          py: 0.75,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          transition: "all 0.3s ease",
                          width: "183px",
                          "&:focus-within": {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 4px 16px ${alpha(
                                theme.palette.primary.main,
                                0.2
                            )}`,
                          },
                        }}
                    >
                      <InputBase
                          ref={searchInputRef}
                          autoFocus
                          placeholder={t("search")}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyDown={handleSearchSubmit}
                          fullWidth
                          sx={{
                            fontSize: 16,
                            fontWeight: 400,
                            lineHeight: "120%",
                            borderRadius: "8px",
                            border: "none",
                            boxShadow: "none",
                            fontFamily: "'Roboto Condensed', Arial, sans-serif",
                            outline: "none",
                            "& input": {
                              border: "none !important",
                              boxShadow: "none !important",
                              outline: "none !important",
                              background: "none",
                              padding: 0,
                            },
                            "&:before, &:after, & fieldset": {
                              display: "none !important",
                              border: "none !important",
                            },
                            "& .MuiInputBase-input": {
                              border: "none !important",
                              boxShadow: "none !important",
                              outline: "none !important",
                              background: "none",
                              padding: 0,
                            },
                          }}
                      />
                    </Paper>

                    <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: "#ffffff",
                          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            transform: "scale(1.05)",
                          },
                          "&:active": {
                            transform: "scale(0.95)",
                          },
                        }}
                        onClick={handleSearchClose}
                        role="button"
                        aria-label="close search"
                    >
                      <CloseMenuIcon
                          color="#666"
                          width={20}
                          height={20}
                          strokeWidth={2.5}
                          style={{ width: 20, height: 20 }}
                      />
                    </Box>
                  </Stack>

                  <Box
                      sx={{
                        position: "absolute",
                        top: "48px",
                        left: 0,
                        right: 0,
                        zIndex: 1203,
                      }}
                  >
                    <SearchResults
                        searchQuery={searchQuery}
                        searchResults={searchResults}
                        loading={loading}
                        onProductClick={handleProductClick}
                    />
                  </Box>
                </Box>
            )}
          </Box>
        </ClickAwayListener>
      </Stack>
  );
};

export default HeaderSearch;